import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface SermonSummaryMetadata {
  mainPoints?: string[];
  keyScriptures?: string[];
  practicalApplications?: string[];
  prayerPoints?: string[];
  discussionQuestions?: string[];
}

interface SermonSummary {
  id: string;
  sermon_id: string | null;
  bulletin_summary: string | null;
  quotes: string[];
  metadata?: SermonSummaryMetadata | null;
  archived: boolean;
  created_at: string;
}

interface CreateSermonSummaryData {
  sermon_id?: string;
  bulletin_summary?: string;
  quotes?: string[];
  metadata?: SermonSummaryMetadata;
}

export function useSermonSummaries() {
  const [summaries, setSummaries] = useState<SermonSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const loadSummaries = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error: fetchError } = await supabase
        .from('sermon_summaries')
        .select('*')
        .eq('user_id', user.id)
        .eq('archived', showArchived)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setSummaries(data || []);
    } catch (err) {
      console.error('Error loading sermon summaries:', err);
      setError(err instanceof Error ? err.message : 'Failed to load sermon summaries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSummaries();
  }, [showArchived]);

  const saveSummary = async (sermon_id: string, data: CreateSermonSummaryData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Try to save with metadata if the column exists, otherwise save without it
      const insertData: Record<string, unknown> = {
        sermon_id,
        bulletin_summary: data.bulletin_summary,
        quotes: data.quotes || [],
        user_id: user.id
      };
      
      // Only include metadata if it has content (column may not exist yet)
      if (data.metadata && Object.keys(data.metadata).length > 0) {
        insertData.metadata = data.metadata;
      }

      let savedSummary;
      let saveError;
      
      // Try with metadata first
      const result = await supabase
        .from('sermon_summaries')
        .insert([insertData])
        .select()
        .single();
      
      if (result.error?.message?.includes('metadata')) {
        // Column doesn't exist, retry without metadata
        delete insertData.metadata;
        const retryResult = await supabase
          .from('sermon_summaries')
          .insert([insertData])
          .select()
          .single();
        savedSummary = retryResult.data;
        saveError = retryResult.error;
      } else {
        savedSummary = result.data;
        saveError = result.error;
      }

      if (saveError) throw saveError;
      await loadSummaries();
      return savedSummary;
    } catch (err) {
      console.error('Error saving sermon summary:', err);
      throw err;
    }
  };

  const toggleArchive = async (id: string, currentState: boolean) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error: updateError } = await supabase
        .from('sermon_summaries')
        .update({ archived: !currentState })
        .eq('id', id)
        .eq('user_id', user.id);

      if (updateError) throw updateError;
      await loadSummaries();
    } catch (err) {
      console.error('Error toggling archive:', err);
      throw err;
    }
  };

  const deleteSummary = async (id: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error: deleteError } = await supabase
        .from('sermon_summaries')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (deleteError) throw deleteError;
      await loadSummaries();
    } catch (err) {
      console.error('Error deleting sermon summary:', err);
      throw err;
    }
  };

  return {
    summaries,
    loading,
    error,
    showArchived,
    setShowArchived,
    saveSummary,
    toggleArchive,
    deleteSummary,
    refresh: loadSummaries
  };
}